"use client";

import { useRef, useState } from "react";
import {
  ErrorNotice,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { downloadPng, downloadSvg } from "@/lib/tool-kit/download";
import {
  addon5Checksum,
  addon5Modules,
  EAN5_PARITY,
  EAN13_PARITY,
  ean13Modules,
  modulesToBars,
  usdPriceAddon,
} from "@/lib/tool-kit/identifiers/ean";
import {
  analyzeIsbn,
  hyphenatedIsbn13,
  type IsbnAnalysis,
} from "@/lib/tool-kit/identifiers/isbn";

/** English strings. A translated content file overrides these through `ui`. */
export const ISBN_BARCODE_GENERATOR_STRINGS = {
  isbnLabel: "ISBN",
  isbnPlaceholder: "978-0-306-40615-7",
  isbnHint:
    "ISBN-13, or an ISBN-10 to convert. Type it with its hyphens to print them above the bars.",
  addonLabel: "Price add-on (EAN-5)",
  addonNone: "None",
  addonUsd: "US dollar price",
  addonNoPrice: "No suggested price (90000)",
  addonCustom: "Other five-digit code",
  priceLabel: "Price in US dollars",
  pricePlaceholder: "12.99",
  priceHint:
    "Encoded as 5 + the price in cents: $12.99 → 51299. $100 or more → 59999.",
  customLabel: "Five-digit add-on",
  customPlaceholder: "51299",
  emptyPrompt: "Enter an ISBN to draw its barcode.",
  badLength: "An ISBN has 10 or 13 digits; this one has {count}.",
  badCharacters: "Only digits, hyphens and a final X belong in an ISBN.",
  badX: "X can only be the check digit of an ISBN-10 — the last character.",
  badPrefix:
    "A book barcode starts with 978 or 979; {prefix} is not an ISBN prefix.",
  badCheck:
    "The check digit should be {expected}, not {actual}. A barcode with the wrong check digit will not scan — check the number against the copyright page.",
  badPrice: "Enter the price as a number of dollars, such as 12.99.",
  badCustom: "The add-on must be exactly five digits.",
  converted:
    "Converted from ISBN-10 {isbn10}: book barcodes always carry the 13-digit ISBN.",
  noHyphens:
    "Printed without hyphens. Type the ISBN with its hyphens, as on the copyright page, to show them above the bars.",
  results: "Barcode",
  svgLabel: "EAN-13 barcode for ISBN {isbn}",
  isbnCaption: "ISBN {isbn}",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  workingHeading: "How the bars encode the number",
  guardsLine:
    "Guard bars frame the symbol: 101 at each end and 01010 in the middle. Each digit is 7 modules wide — two bars and two spaces.",
  parityLine:
    "The first digit, {digit}, has no bars of its own. It sets which of the next six digits use set L or set G: {pattern}. The last six all use set R.",
  addonLine:
    "Add-on {code}: 3 × ({odd}) + 9 × ({even}) = {total}, which ends in {checksum}, so its digits use {pattern}.",
  printNote:
    "The SVG is sized for 100% print: {width} mm wide at the standard 0.33 mm module. Print between 80% and 200% and never stretch one direction only.",
} as const;

type Strings = typeof ISBN_BARCODE_GENERATOR_STRINGS;
type AddonMode = "none" | "usd" | "noPrice" | "custom";

const RE_FIVE_DIGITS = /^\d{5}$/;
const MODULE_MM = 0.33;
const QUIET_LEFT = 11;
const QUIET_RIGHT = 7;
const ADDON_GAP = 9;
const ADDON_QUIET = 5;
const ADDON_WIDTH = 47;
const BARS_TOP = 14;
const BAR_BOTTOM = 83;
const GUARD_BOTTOM = 88;
const HEIGHT = 96;
const FONT = "OCR-B, 'OCR B', 'Courier New', monospace";

/** Guard modules of the 95 in an EAN-13: they run down into the digit line. */
function isGuardModule(index: number): boolean {
  return index < 3 || (index >= 45 && index < 50) || index >= 92;
}

function problemText(analysis: IsbnAnalysis, strings: Strings): string {
  switch (analysis.problem) {
    case "length":
      return t(strings.badLength, { count: analysis.compact.length });
    case "x-position":
      return strings.badX;
    case "prefix":
      return t(strings.badPrefix, { prefix: analysis.compact.slice(0, 3) });
    case "check-digit":
      return t(strings.badCheck, {
        expected: analysis.expectedCheck ?? "",
        actual: analysis.compact.at(-1) ?? "",
      });
    default:
      return strings.badCharacters;
  }
}

/** The five add-on digits, "" for none, or null when the reader's entry is unusable. */
function addonCode(
  mode: AddonMode,
  price: string,
  custom: string
): string | null {
  if (mode === "none") {
    return "";
  }
  if (mode === "noPrice") {
    return "90000";
  }
  if (mode === "usd") {
    const dollars = parseNumberInput(price);
    return dollars === null ? null : usdPriceAddon(dollars);
  }
  const code = custom.trim();
  return RE_FIVE_DIGITS.test(code) ? code : null;
}

/**
 * Where each human-readable digit sits: digits 2–7 under the left half, 8–13 under the
 * right (past the 5-module centre guard), add-on digits above the add-on's bars.
 */
function digitPositions(
  digits: string,
  addon: string,
  addonX: number
): { id: string; digit: string; x: number; y: number }[] {
  const main = [...digits.slice(1)].map((digit, index) => ({
    id: `d${index + 1}`,
    digit,
    x: QUIET_LEFT + 3 + 7 * index + (index < 6 ? 0 : 5) + 3.5,
    y: 92,
  }));
  const extra = [...addon].map((digit, index) => ({
    id: `a${index}`,
    digit,
    x: addonX + 4 + 9 * index + 3.5,
    y: 23,
  }));
  return [...main, ...extra];
}

function Barcode({
  digits,
  addon,
  caption,
  label,
  svgRef,
}: {
  digits: string;
  addon: string;
  caption: string;
  label: string;
  svgRef: React.RefObject<SVGSVGElement | null>;
}) {
  const main = ean13Modules(digits);
  const width = symbolWidth(addon);
  const addonX = QUIET_LEFT + 95 + ADDON_GAP;
  const text = {
    fill: "#000",
    fontFamily: FONT,
    fontSize: 9,
    textAnchor: "middle" as const,
  };
  return (
    <svg
      aria-label={label}
      className="h-auto w-full max-w-md bg-white"
      height={`${(HEIGHT * MODULE_MM).toFixed(2)}mm`}
      ref={svgRef}
      role="img"
      viewBox={`0 0 ${width} ${HEIGHT}`}
      width={`${(width * MODULE_MM).toFixed(2)}mm`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect fill="#fff" height={HEIGHT} width={width} x={0} y={0} />
      {/* The ISBN line is set smaller than the digits so it fits over the bars. */}
      <text {...text} fontSize={7.5} x={QUIET_LEFT + 47.5} y={10}>
        {caption}
      </text>
      <g fill="#000" shapeRendering="crispEdges">
        {modulesToBars(main).map(([start, size]) => (
          <rect
            height={
              (isGuardModule(start) ? GUARD_BOTTOM : BAR_BOTTOM) - BARS_TOP
            }
            key={start}
            width={size}
            x={QUIET_LEFT + start}
            y={BARS_TOP}
          />
        ))}
        {addon
          ? modulesToBars(addon5Modules(addon)).map(([start, size]) => (
              <rect
                height={GUARD_BOTTOM - 26}
                key={`addon-${start}`}
                width={size}
                x={addonX + start}
                y={26}
              />
            ))
          : null}
      </g>
      <text {...text} x={QUIET_LEFT - 5} y={92}>
        {digits[0]}
      </text>
      {digitPositions(digits, addon, addonX).map((place) => (
        <text {...text} key={place.id} x={place.x} y={place.y}>
          {place.digit}
        </text>
      ))}
    </svg>
  );
}

function addonWorking(code: string, strings: Strings): string {
  const d = [...code].map(Number);
  return t(strings.addonLine, {
    code,
    odd: `${d[0]} + ${d[2]} + ${d[4]}`,
    even: `${d[1]} + ${d[3]}`,
    total: 3 * (d[0] + d[2] + d[4]) + 9 * (d[1] + d[3]),
    checksum: addon5Checksum(code),
    pattern: EAN5_PARITY[addon5Checksum(code)],
  });
}

function symbolWidth(addon: string): number {
  return (
    QUIET_LEFT +
    95 +
    (addon ? ADDON_GAP + ADDON_WIDTH + ADDON_QUIET : QUIET_RIGHT)
  );
}

function BarcodeResult({
  analysis,
  digits,
  addon,
  hyphenated,
  strings,
}: {
  analysis: IsbnAnalysis;
  digits: string;
  addon: string;
  hyphenated: string | null;
  strings: Strings;
}) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const shown = hyphenated ?? digits;
  const fileBase = `isbn-${digits}`;
  return (
    <div className={`${RESULT_CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <Barcode
        addon={addon}
        caption={t(strings.isbnCaption, { isbn: shown })}
        digits={digits}
        label={t(strings.svgLabel, { isbn: shown })}
        svgRef={svgRef}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            svgRef.current && downloadSvg(`${fileBase}.svg`, svgRef.current)
          }
          type="button"
        >
          {strings.downloadSvg}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            svgRef.current &&
            downloadPng(`${fileBase}.png`, svgRef.current, { scale: 8 })
          }
          type="button"
        >
          {strings.downloadPng}
        </button>
      </div>
      {analysis.kind === "isbn10" ? (
        <p className="text-muted-foreground text-sm">
          {t(strings.converted, { isbn10: analysis.compact })}
        </p>
      ) : null}
      {hyphenated ? null : (
        <p className="text-muted-foreground text-sm">{strings.noHyphens}</p>
      )}
      <div className="space-y-1 text-muted-foreground text-sm">
        <p className="font-medium text-foreground">{strings.workingHeading}</p>
        <p>{strings.guardsLine}</p>
        <p>
          {t(strings.parityLine, {
            digit: digits[0],
            pattern: EAN13_PARITY[Number(digits[0])],
          })}
        </p>
        {addon ? <p>{addonWorking(addon, strings)}</p> : null}
        <p>
          {t(strings.printNote, {
            width: (symbolWidth(addon) * MODULE_MM).toFixed(2),
          })}
        </p>
      </div>
    </div>
  );
}

function AddonFields({
  mode,
  price,
  custom,
  onPrice,
  onCustom,
  strings,
}: {
  mode: AddonMode;
  price: string;
  custom: string;
  onPrice: (value: string) => void;
  onCustom: (value: string) => void;
  strings: Strings;
}) {
  if (mode === "usd") {
    return (
      <TextField
        hint={strings.priceHint}
        id="isbnbar-price"
        label={strings.priceLabel}
        numeric
        onChange={onPrice}
        placeholder={strings.pricePlaceholder}
        value={price}
      />
    );
  }
  if (mode === "custom") {
    return (
      <TextField
        id="isbnbar-addon"
        label={strings.customLabel}
        mono
        onChange={onCustom}
        placeholder={strings.customPlaceholder}
        value={custom}
      />
    );
  }
  return null;
}

export default function IsbnBarcodeGeneratorWidget() {
  const strings = useToolUi(ISBN_BARCODE_GENERATOR_STRINGS);
  const [isbn, setIsbn] = useState("");
  const [mode, setMode] = useState<AddonMode>("none");
  const [price, setPrice] = useState("");
  const [custom, setCustom] = useState("");

  const analysis = isbn.trim() === "" ? null : analyzeIsbn(isbn);
  const digits = analysis?.valid ? (analysis.isbn13 ?? "") : "";
  const addon = addonCode(mode, price, custom);
  const addonEntered =
    mode === "usd" ? price.trim() !== "" : custom.trim() !== "";
  const addonError = mode === "usd" ? strings.badPrice : strings.badCustom;

  return (
    <section className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.isbnHint}
          id="isbnbar-isbn"
          label={strings.isbnLabel}
          mono
          onChange={setIsbn}
          placeholder={strings.isbnPlaceholder}
          value={isbn}
        />
        <SelectField
          id="isbnbar-addon-mode"
          label={strings.addonLabel}
          onChange={setMode}
          options={[
            { value: "none", label: strings.addonNone },
            { value: "usd", label: strings.addonUsd },
            { value: "noPrice", label: strings.addonNoPrice },
            { value: "custom", label: strings.addonCustom },
          ]}
          value={mode}
        />
      </div>
      <AddonFields
        custom={custom}
        mode={mode}
        onCustom={setCustom}
        onPrice={setPrice}
        price={price}
        strings={strings}
      />
      <div aria-live="polite" className="space-y-4">
        {analysis === null ? <Notice>{strings.emptyPrompt}</Notice> : null}
        {analysis && !analysis.valid ? (
          <ErrorNotice>{problemText(analysis, strings)}</ErrorNotice>
        ) : null}
        {addon === null && addonEntered ? (
          <ErrorNotice>{addonError}</ErrorNotice>
        ) : null}
        {analysis && digits && addon !== null ? (
          <BarcodeResult
            addon={addon}
            analysis={analysis}
            digits={digits}
            hyphenated={hyphenatedIsbn13(isbn)}
            strings={strings}
          />
        ) : null}
      </div>
    </section>
  );
}
